import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatSwedishPhone, isValidSwedishPhone } from '@/lib/phoneUtils';
import { Search, Camera, CheckCircle2, AlertCircle, ArrowLeft, Minus, X, Loader2, RotateCcw, Building2, User } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useQRScanner } from '@/hooks/useQRScanner';
import { useCompanyRequests } from '@/hooks/useCompanyRequests';
import { 
  fetchAllLunchCards, 
  findCardById, 
  findCardByPhone, 
  parseQRData, 
  LunchCardAPI,
  DEMO_PHONE_NUMBERS,
  createDemoCard,
  createDemoCompanyCard,
  decrementLunchCard
} from '@/lib/lunchCardService';

const PersonalKassa = () => {
  const [lunchCards, setLunchCards] = useState<LunchCardAPI[]>([]);
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [searchPhone, setSearchPhone] = useState('');
  const [selectedCard, setSelectedCard] = useState<LunchCardAPI | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [scanMode, setScanMode] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Hämtar företagsförfrågningar för notificering i menyraden
  const { data: requests = [] } = useCompanyRequests();
  const unhandledCount = requests.filter(r => r.status !== 'handled').length;

  // Fetch all cards on mount
  useEffect(() => {
    const loadCards = async () => {
      setIsLoadingCards(true);
      try {
        const cards = await fetchAllLunchCards();
        setLunchCards(cards);
      } catch (error) {
        console.error('Failed to load cards:', error);
        // Use demo cards as fallback
        setLunchCards([
          createDemoCard('0706349637', 'demo-private-0706349637'),
          createDemoCard('0702011393', 'demo-private-0702011393'),
          createDemoCompanyCard('0706349637', 'demo-company-0706349637'),
        ]);
        toast.error('Kunde inte hämta lunchkort från servern');
      } finally {
        setIsLoadingCards(false);
      }
    };
    loadCards();
  }, []);

  // Parse QR code and find lunch card by ID
  const handleQRScanSuccess = useCallback((decodedText: string) => {
    console.log('QR Code scanned:', decodedText);
    
    const parsed = parseQRData(decodedText);
    
    if (!parsed) {
      setScanError('Ogiltig QR-kod. Använd en giltig lunchkorts-QR.');
      return;
    }

    const card = findCardById(lunchCards, parsed.id);
    
    if (card) {
      setSelectedCard(card);
      setScanMode(false);
      setScanError(null);
      toast.success(parsed.type === 'company' ? 'Företagskort skannat!' : 'QR-kod skannad!');
    } else {
      setScanError(`Inget lunchkort hittades med ID: ${parsed.id}`);
    }
  }, [lunchCards]);

  const { scannerRef, isActive, isLoading, error, startScanner, stopScanner } = useQRScanner({
    onScanSuccess: handleQRScanSuccess,
  });

  // Start scanner when scan mode is activated
  useEffect(() => {
    if (scanMode && !isActive && !isLoading) {
      startScanner();
    }
  }, [scanMode, isActive, isLoading, startScanner]);

  // Stop scanner when leaving scan mode
  useEffect(() => {
    if (!scanMode && isActive) {
      stopScanner();
    }
  }, [scanMode, isActive, stopScanner]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidSwedishPhone(searchPhone)) {
      toast.error('Ange ett giltigt svenskt mobilnummer');
      return;
    }
    
    const card = findCardByPhone(lunchCards, searchPhone);
    if (card) {
      setSelectedCard(card);
      return;
    }
    
    toast.error('Inget lunchkort hittades');
    setSelectedCard(null);
  };

  const handleCancelScan = () => {
    setScanMode(false);
    setScanError(null);
    stopScanner();
  };

  const handleRetryScan = () => {
    setScanError(null);
    startScanner();
  };

  const handleUseLunch = async () => {
    if (!selectedCard) return;

    if (selectedCard.remaining <= 0) {
      toast.error('Kortet är tomt – inga luncher kvar');
      return;
    }

    if (selectedCard.id.startsWith('demo-')) {
      // Hantera demodata lokalt
      const updatedCard = { ...selectedCard, remaining: selectedCard.remaining - 1 };
      setLunchCards(prev => 
        prev.map(card => card.id === selectedCard.id ? updatedCard : card)
      );
      setSelectedCard(updatedCard);
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
      return;
    }

    try {
      setIsProcessing(true);
      // Update the card via backend
      const updatedCardApi = await decrementLunchCard(selectedCard.id);
      
      if (!updatedCardApi) {
        throw new Error("Ingen data returnerades från backend");
      }

      const updatedCard = { ...selectedCard, remaining: updatedCardApi.remaining };
      
      setLunchCards(prev => 
        prev.map(card => card.id === selectedCard.id ? updatedCard : card)
      );
      setSelectedCard(updatedCard);
      setShowConfirmation(true);
      
      // Auto-dismiss after 3 seconds if not clicked
      setTimeout(() => {
        setShowConfirmation(false);
      }, 3000);
    } catch (error) {
      console.error(error);
      toast.error('Kunde inte dra en lunch från kortet. Försök igen.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismissConfirmation = () => {
    setShowConfirmation(false);
  };

  const resetSearch = () => {
    setSelectedCard(null);
    setSearchPhone('');
    setShowConfirmation(false);
    setScanError(null);
  };

  const displayError = error || scanError;
  
  // Determine if card is company card
  const isCompanyCard = (card: LunchCardAPI) => !!card.companyName;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="container-wide mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="font-display text-xl font-semibold text-foreground">
              Personalvy – Kassa
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/personal/meny">
              <Button variant="ghost">Meny</Button>
            </Link>
            <Link to="/personal/forfragningar">
              <Button variant="secondary" className="relative">
                Förfrågningar
                {unhandledCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center justify-center min-w-[18px]">
                    {unhandledCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container-narrow mx-auto px-4 py-8">
        {/* Loading indicator */}
        {isLoadingCards && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Hämtar lunchkort...</span>
          </div>
        )}

        {/* Confirmation overlay */}
        {showConfirmation && (
          <div 
            className="fixed inset-0 z-50 bg-accent/95 flex items-center justify-center animate-scale-in cursor-pointer"
            onClick={handleDismissConfirmation}
          >
            <div className="text-center text-accent-foreground pointer-events-none">
              <CheckCircle2 size={80} className="mx-auto mb-6 animate-pulse" />
              <h2 className="font-display text-4xl font-bold mb-2">Lunch använd!</h2>
              <p className="text-2xl opacity-80 mb-6">
                {selectedCard?.remaining} luncher kvar
              </p>
              <p className="text-lg opacity-60">
                Tryck var som helst för att fortsätta
              </p>
            </div>
          </div>
        )}

        {!isLoadingCards && !selectedCard ? (
          <div className="space-y-6">
            {/* Camera scan mode */}
            {scanMode ? (
              <div className="card-warm p-6 animate-fade-in">
                {/* QR Scanner view */}
                <div className="relative w-full max-w-md mx-auto aspect-square rounded-2xl overflow-hidden bg-secondary/50 mb-6">
                  {isLoading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10 bg-secondary/80">
                      <Loader2 size={48} className="text-primary animate-spin" />
                      <p className="text-muted-foreground">Startar kamera...</p>
                    </div>
                  )}
                  
                  {displayError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center z-10 bg-secondary/90">
                      <AlertCircle size={48} className="text-destructive" />
                      <p className="text-destructive font-medium">{displayError}</p>
                      <div className="flex flex-col gap-2 w-full max-w-xs">
                        <Button onClick={handleRetryScan} className="w-full">
                          <RotateCcw size={18} />
                          Försök skanna igen
                        </Button>
                        <Button variant="ghost" onClick={handleCancelScan} className="w-full">
                          Sök manuellt via telefonnummer
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Scanner container */}
                  <div 
                    ref={scannerRef} 
                    className="w-full h-full"
                  />
                </div>

                {/* Instructions */}
                {!displayError && (
                  <p className="text-center text-muted-foreground mb-6 text-lg">
                    📷 Rikta kameran mot kundens QR-kod
                  </p>
                )}

                {/* Cancel button */}
                {!displayError && (
                  <Button 
                    variant="ghost" 
                    onClick={handleCancelScan}
                    size="lg"
                    className="w-full text-lg"
                  >
                    <X size={20} />
                    Avbryt
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Primary: Camera scan button */}
                <Button
                  size="xl"
                  className="w-full py-8 text-xl"
                  onClick={() => setScanMode(true)}
                >
                  <Camera size={28} />
                  Skanna QR-kod med kamera
                </Button>

                {/* Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-muted-foreground text-sm">eller sök manuellt</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Secondary: Phone search */}
                <div className="card-warm p-6">
                  <h2 className="font-display text-xl font-semibold text-foreground mb-4">
                    Sök via telefonnummer
                  </h2>
                  <form onSubmit={handleSearch} className="flex gap-3">
                    <Input
                      type="tel"
                      placeholder="070-123 45 67"
                      value={searchPhone}
                      onChange={(e) => setSearchPhone(e.target.value)}
                      className="text-lg flex-1 h-14"
                    />
                    <Button type="submit" size="lg" className="h-14 px-6">
                      <Search size={20} />
                      Sök
                    </Button>
                  </form>
                </div>

                {/* Recent cards */}
                {lunchCards.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">
                      Tillgängliga lunchkort ({lunchCards.length})
                    </h3>
                    <div className="grid gap-2 max-h-[400px] overflow-y-auto pr-2">
                      {lunchCards.slice().reverse().map((card) => (
                        <button
                          key={card.id}
                          onClick={() => setSelectedCard(card)}
                          className="card-warm p-4 text-left hover:border-primary/30 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isCompanyCard(card) ? (
                                <Building2 size={16} className="text-primary" />
                              ) : (
                                <User size={16} className="text-muted-foreground" />
                              )}
                              <span className="font-medium text-foreground">
                                {isCompanyCard(card) 
                                  ? card.companyName 
                                  : formatSwedishPhone(card.phoneNumber)}
                              </span>
                            </div>
                            <span className={cn(
                              "text-sm font-medium",
                              card.remaining > 3 ? "text-accent" : 
                              card.remaining > 0 ? "text-primary" : "text-destructive"
                            )}>
                              {card.remaining} kvar
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : !isLoadingCards && selectedCard ? (
          <div className="animate-fade-in">
            <Button variant="ghost" onClick={resetSearch} className="mb-6">
              ← Ny sökning
            </Button>

            <div className="card-warm p-8 text-center">
              {/* Card type badge */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {isCompanyCard(selectedCard) ? (
                  <>
                    <Building2 size={20} className="text-primary" />
                    <span className="text-sm font-medium text-primary">Företagslunchkort</span>
                  </>
                ) : (
                  <>
                    <User size={20} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Privat lunchkort</span>
                  </>
                )}
              </div>

              <h2 className="font-display text-2xl font-semibold text-foreground mb-2">
                {isCompanyCard(selectedCard) 
                  ? selectedCard.companyName 
                  : formatSwedishPhone(selectedCard.phoneNumber)}
              </h2>
              {isCompanyCard(selectedCard) && (
                <p className="text-muted-foreground text-sm">
                  {formatSwedishPhone(selectedCard.phoneNumber)}
                </p>
              )}
              
              <div className={cn(
                "inline-flex items-center gap-3 px-6 py-4 rounded-xl my-6",
                selectedCard.remaining > 0 ? "bg-accent/10" : "bg-destructive/10"
              )}>
                {selectedCard.remaining > 0 ? (
                  <>
                    <CheckCircle2 size={32} className="text-accent" />
                    <div className="text-left">
                      <p className="text-sm text-muted-foreground">Luncher kvar</p>
                      <p className="text-4xl font-bold text-accent">
                        {selectedCard.remaining}
                        {selectedCard.total && (
                          <span className="text-lg font-normal text-muted-foreground ml-1">
                            / {selectedCard.total}
                          </span>
                        )}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle size={32} className="text-destructive" />
                    <div className="text-left">
                      <p className="text-destructive font-medium">Inga luncher kvar</p>
                      <p className="text-sm text-muted-foreground">Kunden behöver köpa nytt kort</p>
                    </div>
                  </>
                )}
              </div>

              {selectedCard.remaining > 0 && (
                <Button
                  onClick={handleUseLunch}
                  size="xl"
                  disabled={isProcessing}
                  className="w-full max-w-xs py-6 text-lg"
                >
                  {isProcessing ? (
                    <Loader2 size={24} className="animate-spin" />
                  ) : (
                    <Minus size={24} />
                  )}
                  {isProcessing ? 'Drar lunch...' : 'Använd 1 lunch'}
                </Button>
              )}

              <p className="text-xs text-muted-foreground mt-6">
                Köpt: {new Date(selectedCard.purchaseDate).toLocaleDateString('sv-SE')}
              </p>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default PersonalKassa;
